import { useRef } from "react";
import { VideoOffIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { livenessMessage } from "@/lib/streamStaleness";
import { useStreamPlayback } from "@/lib/useStreamPlayback";

// S-SEC-3 (finding #8) — the player no longer reads the access token from JS to
// authorize HLS. The ``hlsUrl`` provided by the API already carries a
// short-lived signed stream token (signed_stream_url_for), which mediamtx's
// HTTP auth callback validates for the read session.
//
// PLAYBACK CORE LIVES IN ``lib/useStreamPlayback``. This component is tile
// chrome only. See that module's header for why: the staleness detector used to
// live HERE and nowhere else, while /live ran a second, detector-less player.

interface CameraFeedPlayerProps {
  hlsUrl?: string | null;
  /** Camera id — enables stream-token re-mint. The signed token in ``hlsUrl``
   *  expires after ~15 min; with a ``cameraId`` the player re-mints a fresh
   *  token (GET /streaming/tokens/{cameraId}) proactively before expiry and
   *  reactively on a 401/403, then re-inits the source without a page reload.
   *  Omit it (legacy call sites) and playback simply dies at token expiry as
   *  before. */
  cameraId?: string;
  /** Tells the player whether the upstream camera is currently online; an
   *  offline camera shouldn't even attempt to load the HLS playlist. */
  online?: boolean;
  /** Display label shown overlaid on the player while it's loading. */
  cameraName?: string;
  className?: string;
  /** Auto-play muted (Chrome / Safari require muted autoplay). */
  autoplay?: boolean;
  /** Show the standard <video> controls. Defaults to true. */
  controls?: boolean;
}


// Was set during the brief self-signed-cert window in Sprint 9. The EC2 nginx
// now has a real Let's Encrypt cert, so no cert-acceptance dance is needed.
const SELF_SIGNED_HINT_ORIGIN: string | undefined = undefined;

export function CameraFeedPlayer({
  hlsUrl,
  cameraId,
  online = true,
  cameraName,
  className,
  autoplay = true,
  controls = true,
}: CameraFeedPlayerProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const { state, liveness, mustClear } = useStreamPlayback({
    videoRef,
    hlsUrl,
    cameraId,
    enabled: online,
    autoplay,
  });

  return (
    <div
      className={cn(
        "relative aspect-video w-full overflow-hidden rounded bg-black",
        className,
      )}
    >
      <video
        ref={videoRef}
        controls={controls}
        playsInline
        muted
        className="h-full w-full object-contain"
      />

      {/* P0 — a stale/never-started stream takes precedence over every other
          state: the picture has been cleared and the overlay is FULLY OPAQUE
          (bg-black, not bg-black/80) so no residual frame can bleed through and
          be mistaken for live video. */}
      {mustClear && online ? (
        <div
          data-testid="stream-unavailable"
          data-liveness={liveness.kind}
          className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black text-center text-sm text-muted-foreground"
        >
          <VideoOffIcon className="h-6 w-6 text-destructive" />
          <span>{livenessMessage(liveness)}</span>
          {cameraName && <span className="text-xs">{cameraName}</span>}
        </div>
      ) : (state === "offline" || state === "error" || state === "loading" || state === "stopped") && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black text-center text-sm text-muted-foreground">
          {state === "loading" ? (
            <>
              <div className="h-2 w-32 animate-pulse rounded bg-white/20" />
              <span>Loading {cameraName ?? "stream"}…</span>
            </>
          ) : state === "error" ? (
            <>
              <VideoOffIcon className="h-6 w-6 text-destructive" />
              <span>Could not load stream</span>
              {cameraName && (
                <span className="text-xs">{cameraName}</span>
              )}
              {SELF_SIGNED_HINT_ORIGIN && (
                <span className="mt-2 max-w-[20rem] text-balance text-xs text-muted-foreground">
                  If this is your first time, the streaming server uses a
                  temporary self-signed certificate.{" "}
                  <a
                    href={SELF_SIGNED_HINT_ORIGIN}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline underline-offset-2"
                  >
                    Open it once
                  </a>{" "}
                  in a new tab, accept the security warning, then reload.
                </span>
              )}
            </>
          ) : (
            <>
              <VideoOffIcon className="h-6 w-6" />
              <span>Camera offline</span>
              {cameraName && (
                <span className="text-xs">{cameraName}</span>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
